import {
  getDefaultPhaseRange,
  handlePagedVisibility,
  isRequiredSatisfied,
  resolvePagedElems,
} from '../_shared/helpers';
import { getSelectedRowsCount } from '../_shared/base/displaymax';
import { showMessageAndOption } from '../_shared/base/validation';

/**
 * Wraps a dynamic import to ensure ChunkLoadError is properly reported to New Relic
 * with the correct error category, preventing it from being logged as generic "Error" and reduce noise
 */
const wrapChunkImport = (importFn) => () =>
  importFn().catch((err) => {
    const error = err instanceof Error ? err : new Error(String(err));
    error.name = 'ChunkLoadError';

    if (window?.newrelic?.noticeError) {
      try {
        window.newrelic.noticeError(error);
      } catch (e) {}
    }

    throw error;
  });

const widgetImportMap = {
  address: wrapChunkImport(() =>
    import(/* webpackChunkName: "Address" */ '../Address')
  ),
  'ai-chat': wrapChunkImport(() =>
    import(/* webpackChunkName: "AIChat" */ '../AIChat')
  ),
  audio: wrapChunkImport(() =>
    import(/* webpackChunkName: "Audio" */ '../Audio')
  ),
  calendar: wrapChunkImport(() =>
    import(/* webpackChunkName: "Calendar" */ '../Calendar')
  ),
  captcha: wrapChunkImport(() =>
    import(/* webpackChunkName: "Captcha" */ '../Captcha')
  ),
  'card-sort': wrapChunkImport(() =>
    import(/* webpackChunkName: "Cardsort" */ '../Cardsort')
  ),
  colorpicker: wrapChunkImport(() =>
    import(/* webpackChunkName: "Colorpicker" */ '../Colorpicker')
  ),
  dropdown: wrapChunkImport(() =>
    import(/* webpackChunkName: "Dropdown" */ '../Dropdown')
  ),
  dyngrid: wrapChunkImport(() =>
    import(/* webpackChunkName: "Dyngrid" */ '../Dyngrid')
  ),
  'dyngrid-check': wrapChunkImport(() =>
    import(/* webpackChunkName: "Dyngrid" */ '../Dyngrid')
  ),
  grid: wrapChunkImport(() => import(/* webpackChunkName: "Grid" */ '../Grid')),
  'image-highlight': wrapChunkImport(() =>
    import(/* webpackChunkName: "ImageHighlight" */ '../ImageHighlight')
  ),
  'text-highlight': wrapChunkImport(() =>
    import(/* webpackChunkName: "TextHighlight" */ '../TextHighlight')
  ),
  matrix: wrapChunkImport(() =>
    import(/* webpackChunkName: "Matrix" */ '../Matrix')
  ),
  'imagemap-multiple': wrapChunkImport(() =>
    import(/* webpackChunkName: "ImagemapMultiple" */ '../ImagemapMultiple')
  ),
  'imagemap-single': wrapChunkImport(() =>
    import(/* webpackChunkName: "ImagemapSingle" */ '../ImagemapSingle')
  ),
  multiple: wrapChunkImport(() =>
    import(/* webpackChunkName: "SingleMultiple" */ '../SingleMultiple')
  ),
  'multiple-colorpicker': wrapChunkImport(() =>
    import(
      /* webpackChunkName: "SingleMultipleColorpicker" */ '../SingleMultipleColorpicker'
    )
  ),
  open: wrapChunkImport(() => import(/* webpackChunkName: "Open" */ '../Open')),
  placement: wrapChunkImport(() =>
    import(/* webpackChunkName: "Placement" */ '../Placement')
  ),
  rank: wrapChunkImport(() => import(/* webpackChunkName: "Rank" */ '../Rank')),
  registration: wrapChunkImport(() =>
    import(/* webpackChunkName: "Registration" */ '../Registration')
  ),
  rule: wrapChunkImport(() => import(/* webpackChunkName: "Rule" */ '../Rule')),
  scale: wrapChunkImport(() =>
    import(/* webpackChunkName: "Scale" */ '../Scale')
  ),
  single: wrapChunkImport(() =>
    import(/* webpackChunkName: "SingleMultiple" */ '../SingleMultiple')
  ),
  'single-colorpicker': wrapChunkImport(() =>
    import(
      /* webpackChunkName: "SingleMultipleColorpicker" */ '../SingleMultipleColorpicker'
    )
  ),
  thermometer: wrapChunkImport(() =>
    import(/* webpackChunkName: "Thermometer" */ '../Thermometer')
  ),
  upload: wrapChunkImport(() =>
    import(/* webpackChunkName: "Upload" */ '../Upload')
  ),
  'video-dial': wrapChunkImport(() =>
    import(/* webpackChunkName: "VideoDial" */ '../VideoDial')
  ),
  video: wrapChunkImport(() =>
    import(/* webpackChunkName: "Video" */ '../Video')
  ),
};

(function ($, widgets, templates) {
  $.extend(widgets, {
    MEDIA_SMALL_DEVICE_MAX_WIDTH: 320,
    MEDIA_SKINNY_MAX_WIDTH: 575,
    MEDIA_FULL_WIDTH: 1020,
    RELAYOUT_PERIOD: 250,
  });

  /**
   * Class from which all widgets inherit.
   *
   * Provides a consistent interface to managing the life-cycle of widgets on
   * the client, whatever the underlying technology driving the widget is.
   *
   * All of `Widget`'s methods are designed to be called via super calls in
   * subclasses. This is to ensure consistent behaviour across widgets.
   */
  var Widget = Gryphon.BaseClass.extend(
    {
      /**
       * Base constructor.
       *
       * Only light operations, like attaching event listeners or binding
       * variables should be done in this function.
       *
       * @param {object} object The question object that describes the instance.
       *     Must specify element_id, the id of the element in which to render,
       *     which is assumed to already exist in the DOM.
       */
      constructor: function WidgetConstructor(object) {
        /**
         * The jQuery-wrapped element that contains this widget.
         * @public
         * @readonly
         */
        this.$el = $('#' + object.element_id);

        if (!object.element_id || !this.$el.length) {
          throw new TypeError('No valid element_id specified!');
        }

        /**
         * @private
         * @readonly
         */
        this._object = object;

        this._resetClasses();
        this._appendClass(object.view);
      },

      /**
       * Renders and inserts the template into the widget's `$el` element.
       */
      render: function render() {
        var object = this.object();
        var html = templates[object.view](object);
        this.$el.html(html);

        // To help screen readers, reset focus to the top of the
        // form after new widgets render
        mainNav.blur();
        $('#main_form').focus();

        // Legacy Color Palette [GRYP-9764]
        // Behind feature flag for following widget types
        var feature_flag_legacy_colors = [
          'dyngrid',
          'dyngrid-check',
          'placement',
          'image-highlight',
          'text-highlight',
        ];
        if (
          !!object.legacy_colors &&
          _.filter(feature_flag_legacy_colors, function (v) {
            return v === object.type;
          }).length > 0
        ) {
          $('html').addClass('legacy-colors');
        } else {
          $('html').removeClass('legacy-colors');
        }

        // Feature flag list so we can
        // iteratively support turning
        // this on for each widget
        var feature_flag_displaymax = [
          'multiple',
          'multiple-colorpicker',
          'card-sort',
        ];
        if (
          object.displaymax &&
          _.filter(feature_flag_displaymax, function (v) {
            return v === object.type;
          }).length > 0
        ) {
          this._initDisplaymax();
        }

        // note: if we already have an error message displayed
        // do not hide the show_on_skip elements or interrupt the
        // click_next event
        var whitelist = [
          'single',
          'multiple',
          'dropdown',
          'grid',
          'grid-check',
          'grid-open',
        ];
        this._object.show_on_skip = _.filter(object.response_options, {
          options: { show_on_skip: true },
        });
        if (
          $('.alert-danger').is(':visible') ||
          !this._object.show_on_skip ||
          (this._object.show_on_skip &&
            this._object.show_on_skip.length == 0) ||
          whitelist.indexOf(object.type) === -1
        ) {
          return;
        }

        // handle our 'show_on_skip' responses
        $(document).on(
          'after_widget_rendered',
          $.proxy(this, '_handleShowOnSkip')
        );
      },

      /**
       * Appends a class to the `html` element with the current widget name
       * for more controlled styling
       *
       * @param {string} widgetName
       */
      _appendClass: function (widgetName) {
        var htmlElement = document.getElementsByTagName('html')[0];
        htmlElement.classList.add('widget-' + widgetName);
      },

      _bindConditionalCheck: function _bindConditionalCheck() {
        if (mainNav) {
          mainNav.nextButton.bind(
            'click keypress',
            $.proxy(this, '_checkConditionals')
          );
        }
      },

      /**
       * Remove all previous classes starting with `widget-`
       */
      _resetClasses: function () {
        var htmlElement = document.getElementsByTagName('html')[0];
        var classes = Array.from(htmlElement.classList);

        classes.forEach(function (className) {
          if (className.startsWith('widget-')) {
            htmlElement.classList.remove(className);
          }
        });
      },

      _handleShowOnSkip: function _handleShowOnSkip() {
        var self = this;
        var el,
          elms = self._object.show_on_skip || [];
        for (var i = 0; i < elms.length; i++) {
          el = $('#' + self._object.element_id + '-' + elms[i].code);

          if (!$('.alert-danger').is(':visible')) {
            el.hide();
          }
        }

        // interrupt the next click event to check for conditionals
        this._bindConditionalCheck();
      },

      _isIgnoredKeypress: function _isIgnoredKeypress(ev) {
        return (
          ev && ev.type === 'keypress' && ev.key !== ' ' && ev.key !== 'Enter'
        );
      },

      isPassed: function isPassed(self, answered, elms, hidden) {
        // we only care about checking when the 'show_on_skip' is
        // present within a question, otherwise, business as usual
        // treat like a SOFT require and allow continuation after initially shown
        if (
          !self._object.show_on_skip ||
          elms.length == 0 ||
          answered.length > 0 ||
          self._object.required === 'SOFT'
        ) {
          return true;
        }
        // rely on require=HARD for further restrictions
        if (hidden.length == 0 && self._object.required !== 'HARD') {
          return true;
        }
        return false;
      },

      _checkConditionals: function _checkConditionals(ev) {
        if (this._isIgnoredKeypress(ev)) return;
        var self = this,
          el,
          elms = self._object.show_on_skip || [];

        var responses = $('#' + self._object.element_id + ' input');
        var answered = _.filter(responses, function (i) {
          return $(i).is(':checked');
        });
        var hidden = _.filter(elms, function (i) {
          return $('#' + self._object.element_id + '-' + i.code).is(':hidden');
        });

        if (self.isPassed(self, answered, elms, hidden)) {
          return click_next({ key: ev.key, type: ev.type });
        }

        // show the message and extra response option
        for (var i = 0; i < elms.length; i++) {
          el = $('#' + self._object.element_id + '-' + elms[i].code);
          if (el.css('display') === 'none') {
            showMessageAndOption(self, el);
          }
        }
      },

      /**
       * Manages the behaviour specific to widgets with `displaymax` option set.
       *
       * This is mostly a wrapper around legacy code and its side-effects; call
       * `.applySideEffects()` to initialise the behaviours.
       */
      _initDisplaymax: function _initDisplaymax() {
        this.displaymax_soft_prompts = 0;
        this.applySideEffects();
      },

      // NOTE :: As part of the bigger lift, we'll probably want to deprecate this
      // method used in grid.js
      applySideEffects: function applySideEffects() {
        this.update_paged_buttons();
        // Defined at the widget-level
        mainNav.nextButton.bind('click keypress', $.proxy(this, 'p_next'));
        mainNav.backButton.bind('click keypress', $.proxy(this, 'p_back'));
        // Used for the DynamicRowLayout widget
        $(document).on(
          'after_widget_rendered',
          $.proxy(this, 'setPagedVisibility', false)
        );
      },

      /**
       * Important :: This function does the actual hiding / showing
       * of the elements (response options, subquestions, etc) from
       * the DynamicRowLayout class
       *
       */
      setPagedVisibility: function setPagedVisibility(elems) {
        var self = this;
        elems = resolvePagedElems(self, elems);
        if (!elems) return;

        // setup our range of display indices based on current phase
        // and displaymax value
        var range = getDefaultPhaseRange(self);
        handlePagedVisibility(elems, range);
      },

      // NOTE :: As part of the bigger lift, we'll probably want to deprecate this
      // method used in grid.js
      update_paged_buttons: function update_paged_buttons() {
        if (this._object.back_allowed == undefined) {
          this._object.back_allowed =
            $('#back_button').css('display') != 'none';
        }
        var button_states = {};
        button_states.next_button = true;
        button_states.back_button = this._object.back_allowed;
        var pos = this._object.phase_pos;
        if (pos > 0 || this._object.back_allowed) {
          button_states.back_button = true;
        }
        set_nav_button_visibility(button_states);
      },

      /**
       * Checks required and soft_required and returns based on conditional
       *
       * This can be extended within the widget, but will generally cover
       * most widgets with subquestion types (like grid)
       *
       * Note: Widgets with response option pagination should check once all pages
       * have been displayed before determining whether the required flag is
       * satisfied or not
       */
      // NOTE :: As part of the bigger lift, we'll probably want to deprecate this
      // method used in grid.js
      required_is_satisfied: function required_is_satisfied() {
        return isRequiredSatisfied(this, this._object);
      },

      // NOTE :: As part of the bigger lift, we'll probably want to deprecate this
      // method used in grid.js
      selected_rows_count: function selected_rows_count() {
        return getSelectedRowsCount(this);
      },

      // NOTE :: As part of the bigger lift, we'll probably want to deprecate this
      // method used in grid.js
      visible_rows_count: function visible_rows_count() {
        if (this._object.displaymax && this._object.transpose) {
          // If transposed and displaymax, we need to divide the number of table rows
          // with the total # of table cells to determine visible columns
          // NOTE: This solution is specific to grid type widgets for now
          return (
            this.get_phase_rows().length /
            $('table.grid-layout tbody > tr').length
          );
        }
        // Each widget is responsible for returning the right
        // elements for the `get_phase_rows` method since each
        // widget is constructed differently
        return this.get_phase_rows().length;
      },

      // NOTE :: As part of the bigger lift, we'll probably want to deprecate this
      // method used in grid.js
      display_required_message: function display_required_message(container) {
        var template = Gryphon.templates['displaymax-required-message'];
        var data = { required_message: this._object.required_message };
        $(container).before(template(data));
        if (this._object.soft_required) {
          this.displaymax_soft_prompts = 1;
        }
      },

      // NOTE :: As part of the bigger lift, we'll probably want to deprecate this
      // method used in grid.js
      clear_required_message: function clear_required_message() {
        $('#displaymax-required-error').remove();
      },

      /**
       * Returns a deep copy of the original object passed to the constructor.
       *
       * This is used by the base `render` method, so if a subclass needs to
       * modify the object prior to passing it to its template, it should extend
       * this method. This also prevents the template from modifying the original
       * object.
       */
      object: function object() {
        return _.clone(this._object, true);
      },

      /**
       * Destroy the widget.
       *
       * This is the best place to remove event listeners. Removing DOM nodes
       * added by the view template isn't necessary.
       */
      destroy: function destroy() {
        $(document).off('after_widget_rendered');
      },
    },

    // Static properties
    {
      /**
       * Given a question object representing a new widget instance, return a new
       * instance of the appropriate `Widget` subclass.
       *
       * @param {object} object The question object that describes the instance.
       */
      fromObject: async function fromObject(object) {
        const importWidget = widgetImportMap[object.view];

        if (importWidget) {
          await importWidget();
        }

        // After import, class should now be registered
        const updatedTypeMap = Widget._subClasses[object.type];

        if (updatedTypeMap && object.view in updatedTypeMap) {
          return new updatedTypeMap[object.view](object);
        }

        return null;
      },

      /**
       * Register a `Widget` subclass with a widget type so that it is known to
       * `Widget.fromObject`.
       *
       * Depends on static properties of the constructor, `types` and `views`,
       * which are array of strings of types and views supported by the subclass.
       */
      register: function register() {
        var i, j, view, type;
        for (i = 0; (type = this.types[i]); i++) {
          if (!(type in Widget._subClasses)) {
            Widget._subClasses[type] = {};
          }

          var typeMap = Widget._subClasses[type];

          for (j = 0; (view = this.views[j]); j++) {
            if (!(view in templates)) {
              throw new Error("Template '" + view + "' does not exist.");
            }

            if (view in typeMap) {
              throw new Error(
                "Cannot register widget due to existing entry for type '" +
                  type +
                  "' and view '" +
                  view +
                  "'."
              );
            }

            typeMap[view] = this;
          }
        }
      },

      /**
       * The list of types supported by class.
       * Must be overridden by subclasses if they are to be registered.
       */
      types: null,

      /**
       * The list of views supported by a class.
       * Must be overridden by subclasses if they are to be registered.
       */
      views: null,

      /**
       * Private map used by `Widget`.
       * @private
       */
      _subClasses: {},
    }
  );

  widgets.Widget = Widget;
})(jQuery, (Gryphon.widgets = Gryphon.widgets || {}), Gryphon.templates);
